import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import PlusSignIcon from "@hugeicons/core-free-icons/PlusSignIcon"
import MinusSignIcon from "@hugeicons/core-free-icons/MinusSignIcon"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    icon: "⚡",
    question: "Is Zapify better than ManyChat?",
    answer:
      "For Instagram creators, yes. Zapify is purpose-built for Instagram comment-to-DM automation, Story DM automation, AI replies, ask-to-follow flows, lead capture, templates, smart offers, and Instagram growth insights. It is faster to set up and significantly more affordable, especially with lifetime access. ManyChat is better only for businesses that genuinely need automation across multiple channels like WhatsApp, SMS, email, Messenger, TikTok, and Telegram.",
  },
  {
    icon: "🧠",
    question: "What is the best ManyChat alternative for Instagram creators?",
    answer:
      "Zapify is the strongest ManyChat alternative for Instagram creators. It focuses entirely on Instagram comments, DMs, Story automations, AI replies, email collection, ask-to-follow flows, multiple Instagram accounts, team access, smart offers, and growth attribution. Unlike ManyChat, Zapify was built specifically for creators - not agencies or enterprise teams. It's simpler, faster, and more affordable.",
  },
  {
    icon: "💬",
    question: "Does Zapify support Instagram comment-to-DM automation?",
    answer:
      "Yes. Zapify is purpose-built to automatically reply to Instagram comments and move users into DMs with links, offers, content, or personalized AI responses. You can trigger automation on a single post, multiple posts, or any post at once.",
  },
  {
    icon: "📩",
    question: "Does Zapify support Story DM automation?",
    answer:
      "Yes. Zapify supports Story-triggered DM automations, so creators can automate follow-up messages from Story replies and interactions - not just post and Reel comments. This covers the full range of high-intent Instagram touchpoints.",
  },
  {
    icon: "💸",
    question: "Why is ManyChat so expensive compared to Zapify?",
    answer:
      "ManyChat uses active-contact based pricing for newer accounts. Their Pro plan starts at $39/month for 2,500 active contacts, with overage charges beyond that limit. As your audience and campaigns grow, your ManyChat bill grows unpredictably. Zapify's plans are flat from $9.99/month with no overage fees, and Zapify offers a one-time Lifetime Access for $200 with no recurring fees ever.",
  },
  {
    icon: "🎯",
    question: "Is Zapify only for Instagram?",
    answer:
      "Yes - and that's a strength, not a limitation. Zapify is Instagram-focused, which means every feature, template, workflow, and analytics dashboard is optimized for Instagram creator growth. If you need a broad multi-channel automation stack across 6+ platforms, ManyChat may be a better fit. But most creators don't - and they pay a premium for features they'll never use.",
  },
  {
    icon: "🤖",
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify's built-in AI is specifically tuned for Instagram conversations - it understands context, matches your brand voice, and generates replies that feel personal rather than automated. ManyChat offers AI features, but they are not optimized for the Instagram creator use case the way Zapify's are.",
  },
  {
    icon: "🏢",
    question: "Who should use ManyChat instead of Zapify?",
    answer:
      "ManyChat is a better fit for teams that need complex workflow branching, multi-channel automation across WhatsApp, SMS, email, Messenger, TikTok, and Telegram, agency-level client management, or existing ManyChat flows that would be very costly to rebuild. For Instagram-focused creators, the complexity and cost of ManyChat is not worth it.",
  },
  {
    icon: "🔄",
    question: "Can I switch from ManyChat to Zapify?",
    answer:
      "Yes. If your main automations are Instagram comment-to-DM, AI replies, lead capture, and creator workflows, switching to Zapify is straightforward. Most users are live with their first Zapify automation within minutes of signing up. Before switching, audit which of your ManyChat flows are Instagram-specific - those are what Zapify replaces.",
  },
  {
    icon: "🛡️",
    question: "Is Zapify Meta approved?",
    answer:
      "Zapify's current homepage displays 'Approved by Meta.' Zapify uses Instagram's official Graph API and respects all Meta rate limits and compliance requirements.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  manychat: string | boolean
  zapifyNote?: string
  manychatNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "AI & Instagram Intelligence",
    rows: [
      { name: "AI-Powered Comment Replies", zapify: true, manychat: true, zapifyNote: "Built-in, brand voice tuned", manychatNote: "Plan & config dependent" },
      { name: "Brand Voice Customization", zapify: true, manychat: false, zapifyNote: "Tone, voice & reply style", manychatNote: "Not publicly available" },
      { name: "10+ Language Support", zapify: true, manychat: false, zapifyNote: "Native language switching", manychatNote: "Not publicly available" },
    ],
  },
  {
    category: "Instagram Automation",
    rows: [
      { name: "Comment-to-DM Automation", zapify: true, manychat: true },
      { name: "Post & Reel Automation", zapify: true, manychat: true },
      { name: "Story DM Automation", zapify: true, manychat: true, manychatNote: "Verify current plan" },
      { name: "Keyword Triggers", zapify: true, manychat: true },
      { name: "Single-Post Automation", zapify: true, manychat: true },
      { name: "Multiple Selected Posts", zapify: true, manychat: false, manychatNote: "No equivalent" },
      { name: "Any-Post (Evergreen) Automation", zapify: true, manychat: false, manychatNote: "No equivalent" },
      { name: "Ask-to-Follow Flows", zapify: true, manychat: false, manychatNote: "No native equivalent" },
      { name: "Email Capture in DMs", zapify: true, manychat: true },
      { name: "Opening Message Before DM", zapify: true, manychat: true },
    ],
  },
  {
    category: "Templates & Smart Features",
    rows: [
      { name: "Quick-Launch Templates", zapify: true, manychat: true, zapifyNote: "Live in minutes", manychatNote: "More setup required" },
      { name: "Smart Offers / Link Cards", zapify: true, manychat: false, manychatNote: "No native equivalent" },
    ],
  },
  {
    category: "Analytics & Growth",
    rows: [
      { name: "Detailed Instagram Reel Insights", zapify: true, manychat: false, manychatNote: "No equivalent" },
      { name: "Zapify Growth Attribution", zapify: true, manychat: false, zapifyNote: "Followers & leads gained", manychatNote: "No equivalent" },
    ],
  },
  {
    category: "Account & Team",
    rows: [
      { name: "Multiple Instagram Accounts", zapify: true, manychat: true, zapifyNote: "Up to 6 profiles", manychatNote: "Plan dependent" },
      { name: "Team Access", zapify: true, manychat: true, zapifyNote: "From Starter plan", manychatNote: "Plan dependent" },
      { name: "Referral / Affiliate Program", zapify: true, manychat: false, zapifyNote: "20% recurring", manychatNote: "No public program" },
    ],
  },
  {
    category: "Pricing & Access",
    rows: [
      { name: "Predictable Flat Pricing", zapify: true, manychat: false, zapifyNote: "Audience growth = same bill", manychatNote: "Active-contact billing" },
      { name: "Overage Fees", zapify: false, manychat: true, zapifyNote: "None", manychatNote: "Applies beyond plan limit" },
      { name: "Lifetime Access Option", zapify: true, manychat: false, zapifyNote: "$200 one-time - limited spots", manychatNote: "Subscription only, forever" },
      { name: "Free Trial", zapify: true, manychat: true, zapifyNote: "30 days free", manychatNote: "Free tier: 25 contacts" },
      { name: "Multi-Channel Automation", zapify: false, manychat: true, zapifyNote: "Instagram-focused", manychatNote: "6+ channels" },
      { name: "Meta / API Compliance", zapify: true, manychat: true },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingComparison = [
  {
    label: "Free / Trial",
    zapify: "Free 30-day trial",
    manychat: "Free - 25 contacts",
    zapifyNote: "3 automations, 1,000 DMs, 1,000 AI replies",
    manychatNote: "Very limited",
  },
  {
    label: "Entry paid plan",
    zapify: "$9.99/mo",
    manychat: "$39/mo",
    zapifyNote: "Unlimited automations, unlimited DMs, 10K AI replies",
    manychatNote: "2,500 active contacts + overage fees",
  },
  {
    label: "Professional",
    zapify: "$19.99/mo",
    manychat: "$39/mo+",
    zapifyNote: "Unlimited AI replies, 6 Instagram profiles",
    manychatNote: "Cost grows as contacts grow",
  },
  {
    label: "Lifetime Access",
    zapify: "$200 once",
    manychat: "Not available",
    zapifyNote: "All features - no monthly fees ever",
    manychatNote: "Monthly fees forever",
  },
]

// ─── Choose If Data ───────────────────────────────────────────
const chooseZapifyIf = [
  "Want comment-to-DM automation live in minutes with templates",
  "Want AI replies that sound like your brand, not a bot",
  "Want Story DM automation covering all high-intent touchpoints",
  "Want ask-to-follow flows that grow your follower count",
  "Want to collect emails directly from Instagram DM conversations",
  "Want any-post evergreen automation that keeps running without rebuilding",
  "Want Instagram insights and Zapify growth attribution (followers + leads gained)",
  "Manage 2–6 Instagram accounts from one dashboard",
  "Want predictable pricing that doesn't grow with your audience",
  "Want to lock in lifetime access once for $200 instead of paying forever",
  "Want a 20% recurring affiliate commission by referring others",
]

const chooseManyChat = [
  "Need automation across 6+ channels simultaneously (WhatsApp, SMS, email, Messenger, TikTok, Telegram)",
  "Have a full team or agency managing complex, multi-step customer journeys",
  "Need advanced branching logic, custom fields, and conditional flows",
  "Already have years of ManyChat flows and switching costs are very high",
  "Are an agency managing automation for many different clients across many channels",
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/manychat-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Zapify vs ManyChat: Best Instagram DM Automation Tool for Creators",
        description:
          "Compare Zapify vs ManyChat for Instagram DM automation, comment-to-DM replies, AI responses, pricing, setup, and creator workflows. See which tool fits your growth strategy.",
        url: "https://zapify.pro/comparison/manychat-vs-zapify-pro",
        publishedTime: "2026-05-08",
        modifiedTime: "2026-05-08",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      {
        name: "keywords",
        content:
          "Zapify vs ManyChat, ManyChat alternative, Instagram DM automation tool, Instagram comment automation, comment to DM automation, AI Instagram replies, Instagram automation for creators",
      },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/comparison/manychat-vs-zapify-pro" }],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "Zapify vs ManyChat", item: "https://zapify.pro/comparison/manychat-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "Zapify vs ManyChat: Best Instagram DM Automation Tool for Creators",
        description:
          "A practical comparison of Zapify and ManyChat for Instagram DM automation, AI replies, comment-to-DM workflows, pricing, setup, and creator use cases.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-05-08",
        dateModified: "2026-05-08",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: ManyChatComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function ManyChatComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">

        {/* ═══ Section 1: Hero ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated May 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            Zapify vs ManyChat: Best Instagram DM Automation for{" "}
            <span className="gradient-text">Creators</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-2xl mx-auto mb-8 sm:mb-10">
            ManyChat is a powerful multi-channel platform built for agencies and enterprise teams. Zapify is purpose-built for Instagram creators who want fast, AI-powered automation without the complexity or the growing monthly bill.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_ManyChat_Hero", "Start Free No Credit Card")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("feature-table")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Full Comparison
            </Button>
          </div>
        </section>

        {/* ═══ Ask AI to Explain ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "ManyChat", pageUrl: "https://zapify.pro/comparison/manychat-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* ═══ Section 2: Quick Answer / Verdict ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Bottom Line - Which is better, Zapify or ManyChat?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
              <strong className="text-neutral-900 dark:text-white">Zapify is the better choice for Instagram creators.</strong>{" "}
              It's faster to set up, powered by{" "}
              <strong className="bg-clip-text text-transparent bg-gradient-to-r from-brand-accent to-brand-accent">
                built-in AI replies
              </strong>
              {" "}that sound like your brand, and priced for creators - not enterprise teams. ManyChat is a complex, expensive multi-channel platform built for agencies and large businesses that need automation across Instagram, WhatsApp, SMS, email, Messenger, TikTok, and Telegram simultaneously.
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              If Instagram is your main growth channel, Zapify gives you everything you need without the bloat, steep learning curve, or a recurring contact-based bill that grows every time your audience does.
            </p>
          </article>
        </section>

        {/* ═══ Section 4: Feature Comparison Table ═══ */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-3">
              Zapify vs ManyChat -{" "}
              <span className="gradient-text">feature by feature</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              A transparent, side-by-side breakdown of every capability that matters for Instagram creators in 2026.
            </p>
          </div>

          <div className="hidden md:block rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            {featureCategories.map((cat) => (
              <div key={cat.category} className="border-b last:border-b-0 border-neutral-200 dark:border-neutral-800">
                <div className="px-5 py-3 bg-neutral-50/80 dark:bg-neutral-900/80">
                  <p className="text-sm font-bold text-neutral-700 dark:text-neutral-200">{cat.category}</p>
                </div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-neutral-200 dark:border-neutral-800">
                      <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 w-[40%]">Feature</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300 w-[30%]">Zapify</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300 w-[30%]">ManyChat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.rows.map((row) => (
                      <tr key={row.name} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                        <td className="px-5 py-3.5 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{row.name}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                              {typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}
                            </span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                              {typeof row.manychat === "boolean" ? (row.manychat ? "Yes" : "No") : row.manychat}
                            </span>
                            {row.manychatNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.manychatNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <div className="md:hidden space-y-4">
            {featureCategories.map((cat) => (
              <div key={cat.category} className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-4">
                <p className="text-sm font-bold text-neutral-700 dark:text-neutral-200 mb-3">{cat.category}</p>
                <div className="space-y-3">
                  {cat.rows.map((row) => (
                    <div key={row.name} className="rounded-xl border border-neutral-200 dark:border-neutral-700 p-3">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-white mb-2">{row.name}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 p-2">
                          <p className="text-[11px] uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</p>
                          <p className="text-xs font-semibold text-neutral-800 dark:text-neutral-100">
                            {typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}
                          </p>
                        </div>
                        <div className="rounded-lg bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-2">
                          <p className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">ManyChat</p>
                          <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                            {typeof row.manychat === "boolean" ? (row.manychat ? "Yes" : "No") : row.manychat}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ Section 5: Pricing Breakdown ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-3">
              Zapify's creator pricing vs{" "}
              <span className="gradient-text">ManyChat's</span>{" "}
              active-contact billing
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              ManyChat charges $39/month for 2,500 active contacts - with overage fees as your audience grows. Zapify charges flat, predictable rates. Your bill stays the same even if you go viral.
            </p>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify pricing & limits</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">ManyChat pricing & limits</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Best value</th>
                </tr>
              </thead>
              <tbody>
                {pricingComparison.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4">
                      <p className="text-lg sm:text-xl font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</p>
                      {tier.zapifyNote ? <p className="mt-1 text-xs sm:text-sm text-green-700 dark:text-green-300">{tier.zapifyNote}</p> : null}
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <p className="text-lg sm:text-xl font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.manychat}</p>
                      {tier.manychatNote ? <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-300">{tier.manychatNote}</p> : null}
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-[11px] font-bold text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        Zapify
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.35fr_0.95fr] items-start mt-4">
            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6">
              <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">How to read this table</h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                ManyChat starts higher and becomes less predictable as active contacts grow. Zapify stays creator-friendly with lower entry pricing, flat monthly tiers, and a one-time lifetime option.
              </p>
            </div>
            <aside className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-brand-button mb-3">Why creators switch</p>
              <ul className="space-y-3">
                {[
                  "Your cost does not increase every time a campaign performs well.",
                  "You can launch with lower risk: free trial + lower paid entry.",
                  "No overage surprises tied to active contacts.",
                  "Lifetime access lets you cap software cost permanently.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          <div className="mt-8">
            <LifetimeAccessPricing
              spotsLeft={87}
              totalSpots={100}
              price={200}
              originalMonthlyPrice={39}
              onGetAccess={() => handleCTA("CTA_ManyChat_Lifetime_Pricing", "Claim Lifetime Access")}
            />
          </div>
        </section>

        {/* ═══ Section 6: The Problem With ManyChat ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-3">
              The problem with{" "}
              <span className="gradient-text">ManyChat</span>{" "}
              for Instagram creators
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Before you compare features, understand what ManyChat is - and what it isn't.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                title: "ManyChat's pricing punishes audience growth",
                body: "ManyChat's newer accounts are billed on Active Contacts. Their Pro plan covers 2,500 active contacts at $39/month - but every time you run a successful campaign, your contact count grows and so does your bill. There are overage charges once you exceed your plan limit. For creators with fast-growing audiences, ManyChat's costs can spiral quickly and unpredictably.",
              },
              {
                title: "ManyChat is complicated to set up",
                body: "The visual flow builder is powerful but intimidating. Most Instagram creators don't need branching logic, custom fields, conditional flows, and multi-channel sequences - they need their first automation live today. ManyChat requires significant time, effort, and often paid help to build effective flows.",
              },
              {
                title: "ManyChat wasn't built for Instagram-first creators",
                body: "It's a horizontal platform trying to serve everyone - agencies, enterprise ecommerce, and large multi-channel businesses. Zapify was built specifically for Instagram creators and brands from the ground up. That focus shows in everything: the templates, the AI replies, the analytics, and the pricing.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-900/10 p-5 sm:p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-4 h-4 text-red-500 dark:text-red-400" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="font-bold text-neutral-900 dark:text-white text-sm mb-1">{item.title}</p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ Section 7: Choose Zapify / Choose ManyChat ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Choose Zapify */}
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-green-600 dark:text-green-400">Choose Zapify if you…</h3>
              </div>

              <ul className="space-y-2.5">
                {chooseZapifyIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-0.5 flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Choose ManyChat */}
            <div className="rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                </div>
                <h3 className="text-lg font-bold text-neutral-600 dark:text-neutral-400">Choose ManyChat only if you…</h3>
              </div>

              <ul className="space-y-2.5">
                {chooseManyChat.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <div className="bg-neutral-100 dark:bg-neutral-800 rounded-full p-0.5 flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-5 italic">
                For everyone else - especially solo creators, coaches, influencers, and small brands - ManyChat's complexity and cost is a poor trade-off.
              </p>
            </div>
          </div>
        </section>

        {/* ═══ Section 8: What Makes Zapify Different ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-3">
              What makes <span className="gradient-text">Zapify</span> different
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Ten reasons Instagram creators choose Zapify over ManyChat.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                num: "01",
                title: "Built around how Instagram actually works",
                body: "Zapify starts where creators get the most demand: comments and Stories. When someone comments on a Reel or post, Zapify triggers a DM, sends the right link or offer, and continues the conversation automatically. No complex workflow required.",
              },
              {
                num: "02",
                title: "AI replies that sound like you - not a bot",
                body: "Zapify's built-in AI is tuned for Instagram conversations. It understands context, matches your brand voice, and responds in a way that feels personal. ManyChat offers AI features, but they're not tuned for the Instagram creator use case the way Zapify's are.",
              },
              {
                num: "03",
                title: "Ask-to-follow flows that grow your account",
                body: "Zapify's native ask-to-follow flows let you require a follow before delivering gated content, links, or offers. This directly converts engaged commenters into followers - something ManyChat doesn't offer natively.",
              },
              {
                num: "04",
                title: "Lead capture that builds your own audience",
                body: "Zapify collects emails inside Instagram DM conversations, turning one-time engagement into owned leads. You're not just automating replies - you're building a list.",
              },
              {
                num: "05",
                title: "Fastest setup of any Instagram automation tool",
                body: "Zapify's templates mean your first automation can be live in under 5 minutes. Pick a template, add your keyword, set your DM message, done. ManyChat's flow builder requires significant time, knowledge, and often external help.",
              },
              {
                num: "06",
                title: "Instagram and Zapify insights - not vanity metrics",
                body: "Zapify gives you a real view of what your automation is doing: Instagram metrics (reach, views, likes, comments, shares, saves, profile visits, website taps) plus Zapify-specific impact: followers gained and leads captured. ManyChat has no equivalent Instagram-first attribution dashboard.",
              },
              {
                num: "07",
                title: "Single, multiple, or any-post automation",
                body: "Set up automation for one post, a group of selected posts, or every eligible post at once. This evergreen automation approach means you don't have to rebuild flows every time you publish.",
              },
              {
                num: "08",
                title: "Story DM automation",
                body: "Stories are often the highest-intent touchpoint on Instagram - a direct reply from a warm follower. Zapify automates Story DM responses so that high-intent moment is never missed.",
              },
              {
                num: "09",
                title: "Templates and smart offers - ready to launch",
                body: "Zapify's templates cover the most common creator workflows: send a link, share a resource, collect an email, prompt a follow, make an offer. You're not starting from scratch. You're customizing something that already works.",
              },
              {
                num: "10",
                title: "Lifetime Access - the ManyChat pricing killer",
                body: "ManyChat charges you every month, forever, and charges you more as your audience grows. Zapify offers a one-time Lifetime Access for $200 for a limited number of spots. No monthly fees. No overage charges. No subscription that grows with your success.",
              },
            ].map((item) => (
              <div
                key={item.num}
                className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6"
              >
                <div className="flex items-start gap-4">
                  <span className="text-2xl font-black text-brand-button/30 dark:text-brand-button/20 leading-none flex-shrink-0">
                    {item.num}
                  </span>
                  <div>
                    <p className="font-bold text-neutral-900 dark:text-white text-sm mb-1.5">{item.title}</p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ Section 9: FAQ ═══ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 tracking-tight text-center">
              Frequently Asked{" "}
              <span className="gradient-text">Questions</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto mb-8 sm:mb-12 text-center">
              Everything you need to know about Zapify vs ManyChat
            </p>

            <div className="mx-auto max-w-5xl space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div
                    key={faq.question}
                    className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 transition-colors sm:px-6 sm:py-4 dark:bg-card/80"
                    onMouseEnter={() => setOpenFaq(index)}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))}
                      className="flex w-full items-center justify-between gap-3 text-left"
                    >
                  <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{faq.icon}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
                        {isOpen ? (
                          <HugeiconsIcon icon={MinusSignIcon} size={16} color="currentColor" aria-hidden />
                        ) : (
                          <HugeiconsIcon icon={PlusSignIcon} size={16} color="currentColor" aria-hidden />
                        )}
                      </span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ═══ Section 10: Other Comparisons ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other alternatives</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
              { label: "Zapify vs ReplyRush", href: "/comparison/replyrush-vs-zapify-pro" },
              { label: "Zapify vs SuperProfile", href: "/comparison/superprofile-vs-zapify-pro" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200"
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* ═══ Section 11: Bottom CTA ═══ */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Instagram growth without <span className="gradient-text">the overload.</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Start free, get your first automation live in minutes, and lock in lifetime access before spots run out.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => handleCTA("CTA_ManyChat_Bottom", "Start Free with Zapify")}
                className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
              >
                Start Free with Zapify
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free trial &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            ManyChat is a trademark of ManyChat, Inc. Zapify is not affiliated with, endorsed by, or sponsored by ManyChat. Feature and pricing information is based on publicly available information and Zapify research as of May 8, 2026. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>

      </div>
    </div>
  )
}
